import { useState } from 'react';
import { formatIsoDate, formatRubles, useEmployeeFines } from '../../api/records';
import { Icon } from '../../components/Icon';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { AttendanceDeductions } from './PayrollSection';

/**
 * «Нарушения»: виды нарушений должности и нарушения за выплату из файла (раз в 14 дней), а ниже —
 * удержания по турникету: опоздания, ранний уход, прогулы.
 */
export function FinesSection({
  employeeId,
  currentMonth,
  withFileFines = true,
  withDeductions = true,
}: {
  employeeId: string;
  currentMonth: string;
  /** Нарушения из файла: только тем, кто их видит (не администратору). */
  withFileFines?: boolean;
  withDeductions?: boolean;
}) {
  return (
    <section id="fines" className="panel">
      <div className="panel-header">
        <h2>Нарушения</h2>
      </div>
      {withFileFines ? <FileFines employeeId={employeeId} /> : null}
      {withDeductions ? <AttendanceDeductions employeeId={employeeId} currentMonth={currentMonth} /> : null}
    </section>
  );
}

/** Нарушения из файла HR: за что может быть удержание и нарушения за выплату. */
function FileFines({ employeeId }: { employeeId: string }) {
  const [payoutId, setPayoutId] = useState<string | null>(null);
  const fines = useEmployeeFines(employeeId, payoutId);
  const data = fines.data;

  return (
    <>
      {fines.isPending ? <LoadingState label="Загружаю нарушения" /> : null}
      {fines.isError ? <ErrorState error={fines.error} onRetry={() => void fines.refetch()} /> : null}
      {data ? (
        <div className="reg-columns">
          <div className="reg-part">
            <h3>Виды нарушений</h3>
            {data.reasons.length ? (
              <ol className="reg-list-rows">
                {data.reasons.map((reason, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: список только для чтения, порядок задаёт HR
                  <li key={index}>
                    <span className="reg-num">{index + 1}</span>
                    <span className="reg-text">{reason.title}</span>
                    <span className="reg-period">{formatRubles(reason.amountKopecks)}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="muted">Для должности причины не заполнены</p>
            )}
          </div>
          <div className="reg-part">
            {data.payout ? (
              <>
                <h3 className="fine-heading">
                  Нарушения за выплату{' '}
                  {data.payouts.length > 1 ? (
                    <select
                      className="select"
                      aria-label="Выплата"
                      value={data.payout.id}
                      onChange={(event) => setPayoutId(event.target.value)}
                    >
                      {data.payouts.map((payout) => (
                        <option key={payout.id} value={payout.id}>
                          {formatIsoDate(payout.paidOn)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    formatIsoDate(data.payout.paidOn)
                  )}
                </h3>
                <p className="fine-period">
                  Период {formatIsoDate(data.payout.periodFrom)} — {formatIsoDate(data.payout.periodTo)} ·
                  следующее обновление {formatIsoDate(data.payout.nextUpdate)}
                </p>
                {data.items.length ? (
                  <>
                    <div className="table-scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Дата</th>
                            <th>Причина</th>
                            <th className="number">Сумма</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.items.map((item) => (
                            <tr key={item.id}>
                              <td>{formatIsoDate(item.occurredOn)}</td>
                              <td>
                                <strong>{item.reason}</strong>
                              </td>
                              <td className="number bad">{formatRubles(item.amountKopecks)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="fine-total">
                      <span>Итого</span>
                      <strong className="fine-total-sum">{formatRubles(data.totalKopecks)}</strong>
                    </div>
                  </>
                ) : (
                  <p className="fine-none">
                    <Icon name="check" size={16} />
                    Нарушений нет
                  </p>
                )}
              </>
            ) : (
              <EmptyState title="Нарушения за выплаты ещё не загружены" />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
